
import {
  defineComponent, ref, computed, reactive,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, SubscribeResult, SubscriptionChannels } from './types';

export default defineComponent({
  name: 'SubscribeAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const currentPayload = ref<ActionPayload | null>(null);

    // Channel selection state
    const channels = reactive<SubscriptionChannels>({
      email: false,
      sms: false,
      whatsapp: false,
    });

    // Reset channels to default state
    const resetChannels = () => {
      channels.email = false;
      channels.sms = false;
      channels.whatsapp = false;
    };

    // Check if at least one channel is selected
    const hasSelection = computed(() => channels.email || channels.sms || channels.whatsapp);

    // Computed header based on context
    const dialogHeader = computed(() => {
      if (currentPayload.value?.context === 'grouped') {
        return t('myLists.actions.subscribe.headerGrouped');
      }
      return t('myLists.actions.subscribe.headerRow');
    });

    /**
     * Toggle a channel selection
     */
    const toggleChannel = (channel: keyof SubscriptionChannels) => {
      channels[channel] = !channels[channel];
    };

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ActionPayload) => {
      resetChannels();
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      resetChannels();
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = () => {
      if (!currentPayload.value || !hasSelection.value) return;

      const result: SubscribeResult = {
        context: currentPayload.value.context,
        channels: { ...channels },
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      dialogHeader,
      channels,
      hasSelection,
      toggleChannel,
      close,
      confirm,
    };
  },
});
